/**
 * The last `/auth/session` answer, kept so a repeat visit paints at once.
 *
 * The shell's first paint is the account, the project switcher and the list of
 * projects, and all three come from `/auth/session`. A visit after the first
 * draws them from this copy and revalidates, so a slow session answer does not
 * leave the page on a skeleton. The copy is a hint, never authority: the
 * Worker still decides every read, a signed-out answer or a sign-out removes
 * it, and nothing here is read for a public page.
 */

import { projectMemberships, signedInAccount, type ProjectMembership, type SignedInAccount } from './account-session.js';

const KEY = 'matrix.session';

export type CachedSession = { account: SignedInAccount; projects: readonly ProjectMembership[] };

/** The stored answer, or null where there is none, storage is unavailable, or it no longer parses. */
export function readSessionCache(): CachedSession | null {
  try {
    const stored = globalThis.localStorage?.getItem(KEY);
    if (stored === null || stored === undefined) return null;
    const body: unknown = JSON.parse(stored);
    const account = signedInAccount(body);
    const projects = projectMemberships(body);
    return account === null || projects === null ? null : { account, projects };
  } catch {
    return null;
  }
}

export function writeSessionCache(value: CachedSession): void {
  try {
    globalThis.localStorage?.setItem(KEY, JSON.stringify(value));
  } catch {
    // A full or blocked store only costs the next visit its head start.
  }
}

export function clearSessionCache(): void {
  try {
    globalThis.localStorage?.removeItem(KEY);
  } catch {
    // Nothing was stored that could be removed.
  }
}
